import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { Tracking } from '@freelancer/tracking';
import { CookieService } from '@laurentgoudet/ngx-cookie';
import * as md5 from 'blueimp-md5';
import * as Rx from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { WHITELIST_COOKIE } from './abtest.config';
import {
  ProjectExperiments,
  SessionExperiments,
  UserExperiments,
} from './experiments';

export enum ABTestType {
  SESSION_BASED = 1,
  USER_BASED = 2,
  PROJECT_BASED = 3,
}

export interface ABTestApiResponse<T extends keyof Experiments> {
  state: 'in_test';
  test_id: number;
  variation_name: Experiments[T];
}

export type VariationName = string;

export const STORAGE_KEY = 'WEBAPP_ABTEST_CACHE_V1';

type Experiments = SessionExperiments & UserExperiments & ProjectExperiments;

// FIXME(T66231): we should make the A/B test service return a tuple of
// `response | error` instead of throwing exceptions, as handling of these
// later can't be enforced in TypeScript.
//
// For now, make sure you catch any ABTest service failure or bad things will
// happen when the backend is down.
@Injectable()
export class ABTest {
  constructor(
    private auth: Auth,
    private cookies: CookieService,
    private freelancerHttp: FreelancerHttp,
    private tracking: Tracking,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(WHITELIST_COOKIE) private whitelistCookie: string,
  ) {}

  getSessionExperimentVariation<T extends keyof SessionExperiments>(
    experimentName: T,
  ): Rx.Observable<SessionExperiments[T] | undefined> {
    return this.tracking.getSessionId().pipe(
      take(1),
      switchMap(sessionId =>
        this.getVariation(ABTestType.SESSION_BASED, experimentName, sessionId),
      ),
    );
  }

  // userId is optional, as it'll default to the user's own id
  getUserExperimentVariation<T extends keyof UserExperiments>(
    experimentName: T,
    userId?: number,
  ): Rx.Observable<UserExperiments[T] | undefined> {
    const userId$ = userId
      ? Rx.of(userId)
      : this.auth.getUserId().pipe(map(uid => parseInt(uid, 10)));
    return userId$.pipe(
      take(1),
      switchMap(uid =>
        this.getVariation(ABTestType.USER_BASED, experimentName, uid),
      ),
    );
  }

  getProjectExperimentVariation<T extends keyof ProjectExperiments>(
    experimentName: T,
    projectId: number,
  ): Rx.Observable<ProjectExperiments[T] | undefined> {
    return this.getVariation(
      ABTestType.PROJECT_BASED,
      experimentName,
      projectId,
    );
  }

  private getVariation<T extends keyof Experiments>(
    experimentType: ABTestType,
    experimentName: T,
    identifier: string | number,
  ): Rx.Observable<Experiments[T] | undefined> {
    // global test disabling flag, used by automation tools
    if (this.cookies.get('no_abtest') !== undefined) {
      return Rx.of(undefined);
    }
    // test overrides functionality
    const overrides = new URL(window.location.href).searchParams.get(
      'overrides',
    );
    if (overrides) {
      const overridesMap = overrides
        .split(',')
        .reduce<{ [experimentName: string]: Experiments[T] }>(
          (acc, experiment) => {
            const [name, variation] = experiment.split(':');
            acc[name] = variation as Experiments[T];
            return acc;
          },
          {},
        );
      if (overridesMap[experimentName]) {
        return Rx.of(overridesMap[experimentName]);
      }
    }
    // try reading the variation from cache if it exists
    const cacheKey = `${STORAGE_KEY}|${experimentType}|${experimentName}|${identifier}`;
    // skip cache when on the server
    if (isPlatformBrowser(this.platformId)) {
      let variationFromStorage: Experiments[T] | undefined;
      try {
        variationFromStorage =
          (window.sessionStorage.getItem(cacheKey) as Experiments[T]) || '';
      } catch (e) {
        // ignore the errors, e.g. quota is full or security error
        console.error(e);
      }
      if (variationFromStorage) {
        return Rx.of(variationFromStorage);
      }
    }
    // overwise get the variation for the A/B test server
    return this.freelancerHttp
      .post<ABTestApiResponse<T>>('abtest/0.1/enrollments/', {
        id_type: experimentType,
        experiment_name: experimentName,
        id: identifier,
      })
      .pipe(
        map(response => {
          if (response.status !== 'success') {
            return undefined;
          }
          return response.result.variation_name;
        }),
        tap(variationName => {
          // skip cache when on the server or on error
          if (
            variationName !== undefined &&
            isPlatformBrowser(this.platformId)
          ) {
            try {
              window.sessionStorage.setItem(cacheKey, variationName);
            } catch (e) {
              // ignore the errors, e.g. quota is full or security error
              console.error(e);
            }
          }
        }),
        catchError(() => Rx.of(undefined)),
      );
  }

  /**
   * Determines whether or not to enrol a user/project based on a percentage split.
   * Use this function instead of MOD if you want to gradually increase enrolment on an experiment.
   * Enrol/activate the test separately using the appropriate `get*Variation`. function
   *
   * Functionally similar to ABTest::md5sumTestHelper in ABTest.php
   *
   * @param experimentName name of the experiment
   * @param contextId id of user (for user-based tests) or project (for project-based tests) being split on
   * @param testProportion proportion to allow enrolment (eg. 0.2)
   * @returns `true` if the test should be enrolled, false if not
   */
  shouldEnrol<T extends keyof Experiments>(
    experimentName: T,
    contextId: number | string,
    testProportion: number,
  ): boolean {
    const hash = md5(`${contextId}${experimentName}`)
      .toString()
      .substr(0, 8);
    const quotient = parseInt(hash, 16) / 0xffffffff;
    return quotient < testProportion;
  }

  isWhitelistUser() {
    return this.cookies.get(this.whitelistCookie) === 'true';
  }
}
